import { CalendarPeriod } from './school-calendar.types';

/**
 * Pure layout for the school-timetable views. Kept apart from the component so it can be tested
 * without a DOM: which columns there are, which hours the grid spans, and where each booking sits.
 */

export interface CalendarBlock {
  period: CalendarPeriod;
  /** Fractions of the grid's height, 0–1. */
  top: number;
  height: number;
  /** Side-by-side position among the bookings it overlaps: lane / lanes of the width. */
  lane: number;
  lanes: number;
  /** `HH:mm`, clipped to the column's day. */
  startLabel: string;
  endLabel: string;
  past: boolean;
  /**
   * Set on the block that stands in for bookings there was no room for: how many it hides. Its
   * `period` is the first of them, for tracking only.
   */
  overflow?: number;
}

export interface CalendarColumn {
  key: string;
  label: string;
  sublabel: string | null;
  isToday: boolean;
  blocks: CalendarBlock[];
}

export interface CalendarLayout {
  columns: CalendarColumn[];
  startHour: number;
  endHour: number;
}

interface Segment {
  period: CalendarPeriod;
  start: Date;
  end: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 20;
const MIN_SPAN_HOURS = 6;

/**
 * At most this many bookings side by side in a column. A big anlæg can have dozens of rooms busy
 * at once; past a handful the blocks are stripes no one can read from across a hall, so the rest
 * become one "+N" block. A screen narrowed to one hall rarely gets there.
 */
const MAX_LANES = 4;

/**
 * Weekdays as columns, Monday first. Saturday and Sunday are left out when nothing is booked on
 * them — a school timetable is Monday to Friday unless the weekend has something to show.
 */
export function buildWeekLayout(periods: CalendarPeriod[], from: Date, now: Date): CalendarLayout {
  const days = Array.from({ length: 7 }, (_, i) => addDays(startOfDay(from), i));

  const perDay = days.map((day) => ({ day, segments: clip(periods, day) }));
  const shown = perDay.filter(({ day, segments }) => !isWeekend(day) || segments.length > 0);

  const { startHour, endHour } = hourRange(shown.flatMap((d) => d.segments));

  return {
    startHour,
    endHour,
    columns: shown.map(({ day, segments }) => ({
      key: day.toISOString(),
      label: capitalize(day.toLocaleDateString('da-DK', { weekday: 'long' })),
      sublabel: day.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }),
      isToday: sameDay(day, now),
      blocks: placeBlocks(segments, day, startHour, endHour, now),
    })),
  };
}

/** Today with each resource as a column, named as the old screen names it. */
export function buildDayLayout(periods: CalendarPeriod[], day: Date, now: Date): CalendarLayout {
  const dayStart = startOfDay(day);
  const segments = clip(periods, dayStart);
  const { startHour, endHour } = hourRange(segments);

  const byResource = new Map<number, Segment[]>();
  for (const segment of segments) {
    const list = byResource.get(segment.period.resourceId) ?? [];
    list.push(segment);
    byResource.set(segment.period.resourceId, list);
  }

  const collator = new Intl.Collator('da-DK', { numeric: true });

  const columns = [...byResource.entries()].map(([resourceId, list]) => {
    const sample = list[0].period;
    return {
      key: String(resourceId),
      label: sample.resource,
      sublabel: sample.resourceParent || null,
      isToday: true,
      blocks: placeBlocks(list, dayStart, startHour, endHour, now),
    };
  });

  columns.sort(
    (a, b) => collator.compare(a.sublabel ?? '', b.sublabel ?? '') || collator.compare(a.label, b.label),
  );

  return { startHour, endHour, columns };
}

/** Splits columns into pages of at most `perPage`. Always at least one page, possibly empty. */
export function paginate<T>(items: T[], perPage: number): T[][] {
  if (items.length === 0) return [[]];
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += perPage) pages.push(items.slice(i, i + perPage));
  return pages;
}

/** The whole hours the grid spans: earliest start to latest end, at least six hours, within the day. */
export function hourRange(segments: { start: Date; end: Date }[]): { startHour: number; endHour: number } {
  if (segments.length === 0) return { startHour: DEFAULT_START_HOUR, endHour: DEFAULT_END_HOUR };

  let startHour = Math.min(...segments.map((s) => s.start.getHours()));
  let endHour = Math.max(...segments.map((s) => hourCeil(s.end)));

  // Too short a span makes every block a stripe: widen it around the bookings, inside 0–24.
  if (endHour - startHour < MIN_SPAN_HOURS) {
    const missing = MIN_SPAN_HOURS - (endHour - startHour);
    startHour = Math.max(0, startHour - Math.floor(missing / 2));
    endHour = Math.min(24, startHour + MIN_SPAN_HOURS);
    startHour = Math.max(0, endHour - MIN_SPAN_HOURS);
  }

  return { startHour, endHour };
}

/**
 * Positions one column's bookings. Overlapping bookings form a cluster and share its width in
 * lanes; a booking takes the first lane that is free at its start.
 */
function placeBlocks(
  segments: Segment[],
  dayStart: Date,
  startHour: number,
  endHour: number,
  now: Date,
): CalendarBlock[] {
  const gridStart = dayStart.getTime() + startHour * 3600_000;
  const gridMs = (endHour - startHour) * 3600_000;

  const sorted = [...segments].sort(
    (a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime(),
  );

  const blocks: CalendarBlock[] = [];
  let cluster: CalendarBlock[] = [];
  let clusterEnd = -Infinity;
  let laneEnds: number[] = [];

  const closeCluster = () => {
    const lanes = laneEnds.length;
    if (lanes <= MAX_LANES) {
      for (const block of cluster) block.lanes = lanes;
    } else {
      // Keep the first lanes; the rest become one block over the time they span.
      const kept = cluster.filter((b) => b.lane < MAX_LANES - 1);
      const hidden = cluster.filter((b) => b.lane >= MAX_LANES - 1);
      for (const block of kept) block.lanes = MAX_LANES;

      const top = Math.min(...hidden.map((b) => b.top));
      const bottom = Math.max(...hidden.map((b) => b.top + b.height));
      blocks.splice(blocks.length - cluster.length, cluster.length, ...kept, {
        ...hidden[0],
        top,
        height: bottom - top,
        lane: MAX_LANES - 1,
        lanes: MAX_LANES,
        past: hidden.every((b) => b.past),
        overflow: hidden.length,
      });
    }
    cluster = [];
    laneEnds = [];
  };

  for (const segment of sorted) {
    const start = segment.start.getTime();
    const end = segment.end.getTime();

    if (start >= clusterEnd) closeCluster();

    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    clusterEnd = Math.max(clusterEnd, end);

    const top = clamp01((start - gridStart) / gridMs);
    const bottom = clamp01((end - gridStart) / gridMs);

    const block: CalendarBlock = {
      period: segment.period,
      top,
      height: Math.max(bottom - top, 0),
      lane,
      lanes: 1,
      startLabel: hhmm(segment.start),
      endLabel: end >= dayStart.getTime() + DAY_MS ? '24:00' : hhmm(segment.end),
      past: end <= now.getTime(),
    };
    cluster.push(block);
    blocks.push(block);
  }
  closeCluster();

  return blocks;
}

/** The part of each period that falls on `day`. */
function clip(periods: CalendarPeriod[], day: Date): Segment[] {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + DAY_MS;

  return periods
    .map((period) => ({ period, start: new Date(period.start).getTime(), end: new Date(period.end).getTime() }))
    .filter(({ start, end }) => start < dayEnd && end > dayStart)
    .map(({ period, start, end }) => ({
      period,
      start: new Date(Math.max(start, dayStart)),
      end: new Date(Math.min(end, dayEnd)),
    }));
}

function hourCeil(date: Date): number {
  // A segment clipped at midnight ends on the next day's 00:00 — that is hour 24 of this one.
  if (date.getHours() === 0 && date.getMinutes() === 0) return 24;
  return date.getMinutes() > 0 || date.getSeconds() > 0 ? date.getHours() + 1 : date.getHours();
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isWeekend(date: Date): boolean {
  return date.getDay() === 0 || date.getDay() === 6;
}

function hhmm(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
